import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";

const FutureTable = ({ rows = [], futureTradesLoading, refresh }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [originalRow, setOriginalRow] = useState(null);

  if (futureTradesLoading) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", mt: 2 }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper sx={{ p: 2, textAlign: "center", mt: 2 }}>
        No future trades available
      </Paper>
    );
  }
  const handleEdit = (row) => {
    console.log(row, "row+++++");
    const data = {
      id: row.id,
      stop_loss: row.stop_loss || "",
      target_amount: row.target_amount || "",
      trade_status: row.trade_status || "",
      cancel_reason: "",
    };
    setEditRow(data);
    setOriginalRow(data);
    setOpenDialog(true);
  };

  const handleChange = (e) => {
    setEditRow({ ...editRow, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {};

      if (editRow.stop_loss !== originalRow.stop_loss) {
        payload.stop_loss = editRow.stop_loss;
      }

      if (editRow.target_amount !== originalRow.target_amount) {
        payload.target_amount = editRow.target_amount;
      }

      if (editRow.trade_status !== originalRow.trade_status) {
        payload.trade_status = editRow.trade_status;

        if (editRow.trade_status === "CANCEL") {
          payload.cancel_reason = editRow.cancel_reason;
        }
      }

      if (Object.keys(payload).length === 0) {
        setOpenDialog(false);
        return;
      }

      const response = await axiosConfig({
        method: "post",
        url: `/future-signal-trade/${editRow.id}`,
        data: payload,
      });

      toast.success(response?.data?.response || "Changes saved successfully");
      refresh && refresh();
      setOpenDialog(false);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error.message ||
        "Failed to save changes";
      console.error("Error saving changes:", errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Symbol</b>
              </TableCell>
              <TableCell>
                <b>Trade Symbol</b>
              </TableCell>
              <TableCell>
                <b>Type</b>
              </TableCell>
              <TableCell>
                <b>Lot</b>
              </TableCell>
              <TableCell>
                <b>Date</b>
              </TableCell>
              <TableCell>
                <b>Expiry Date</b>
              </TableCell>
              <TableCell>
                <b>Entry</b>
              </TableCell>
              <TableCell>
                <b>SL</b>
              </TableCell>
              <TableCell>
                <b>Target</b>
              </TableCell>
              <TableCell>
                <b>Duration</b>
              </TableCell>
              <TableCell>
                <b>Status</b>
              </TableCell>
              <TableCell>
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.symbol_name}</TableCell>
                <TableCell>{row.trade_symbol}</TableCell>
                <TableCell>{row.trade_type}</TableCell>
                <TableCell>{row.lot_size}</TableCell>
                <TableCell>
                  {new Date(row.date_of_recommendation).toLocaleDateString(
                    "en-GB"
                  )}
                </TableCell>
                <TableCell>
                  {new Date(row.expire_date).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell>{row.entry_price}</TableCell>
                <TableCell>{row.stop_loss}</TableCell>
                <TableCell>{row.target_amount}</TableCell>
                <TableCell>{row.duration}</TableCell>
                <TableCell>{row.trade_status}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleEdit(row)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <DialogTitle>Edit Trade</DialogTitle>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Stop Loss"
            name="stop_loss"
            value={editRow?.stop_loss ?? ""}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Target Amount"
            name="target_amount"
            value={editRow?.target_amount ?? ""}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            select
            label="Trade Status"
            name="trade_status"
            value={editRow?.trade_status ?? ""}
            onChange={handleChange}
            fullWidth
          >
            <MenuItem value="OPEN">OPEN</MenuItem>
            <MenuItem value="CANCEL">CANCEL</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>

          {editRow?.trade_status === "CANCEL" && (
            <TextField
              label="Cancel Reason"
              name="cancel_reason"
              value={editRow.cancel_reason}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="success"
            onClick={() => setOpenDialog(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FutureTable;
